import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ModalController } from '@ionic/angular/standalone';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { from, of } from 'rxjs';
import { catchError, filter, map, mergeMap } from 'rxjs/operators';
import { CommunicationsRecordModal } from 'src/app/pages/patient/modals/communications-record/communications-record.modal';
import { ClientsService } from 'src/app/services/clients.service';
import { ErrorService } from 'src/app/services/error.service';
import { PatientsService } from 'src/app/services/patients.service';
import { ClientTypeName } from 'src/app/types/aggregate-graph.types';
import { CommsPanelChildType } from 'src/app/types/patient.types';
import { clientErrored } from '../error/error.actions';
import * as patientGraphActions from '../patient-graph/patient-graph.actions';
import { patientId } from '../patient-graph/patient-graph.selectors';
import * as patientActions from '../patient/patient.actions';
import { patientSelected, selectedCommsPanelChildType } from '../patient/patient.selectors';
import { isMobile } from '../platform/platform.selectors';
import { AppState } from '../reducers';
import { hideMigrationFeatures } from '../user-graph/user-graph.selectors';
import * as patientCommsActions from './patient-comms.actions';

@Injectable()
export class PatientCommsEffects {

	constructor(private store: Store<AppState>, private actions$: Actions, private modalController: ModalController, private patientsService: PatientsService, private clientsService: ClientsService, private errorService: ErrorService) {
	}

	isMobileChanged$ = createEffect(() =>
		this.store.select(isMobile).pipe(
			concatLatestFrom(() => [this.store.select(patientSelected), this.store.select(selectedCommsPanelChildType), this.store.select(hideMigrationFeatures)]),
			filter(([isMobile, patientSelected, childType]) => patientSelected && (isMobile || childType === CommsPanelChildType.None)),
			map(([isMobile, , , hideMigrationFeatures]) => {
				return patientActions.commsPanelChildSelected({ childType: isMobile ? CommsPanelChildType.None : (hideMigrationFeatures ? CommsPanelChildType.ReferringVet : CommsPanelChildType.CommunicationsLog) })
			}),
		)
	);

	presentCommunicationRecordModalRequested$ = createEffect(() =>
		this.actions$.pipe(
			ofType(patientCommsActions.presentCommunicationRecordModalRequested),
			mergeMap(action => from(this.modalController.create({
				component: CommunicationsRecordModal,
				componentProps: { record: action.record },
				backdropDismiss: false
			})).pipe(
				map(modal => modal.present()))
			)
		),
		{ dispatch: false }
	);

	saveCommunicationRecordRequested$ = createEffect(() =>
		this.actions$.pipe(
			ofType(patientCommsActions.saveCommunicationRecordRequested),
			concatLatestFrom(() => [this.store.select(patientId)]),
			mergeMap(([action, patientId]) => this.patientsService.saveCommunicationRecord(patientId, action.record).pipe(
				map(record => patientCommsActions.saveCommunicationRecordCompleted({ patientId, record })),
				catchError((error: HttpErrorResponse) => of(clientErrored({ toastMessage: 'Save communication record failed', errorMessage: this.errorService.getErrorMessage(error) })))
			))
		)
	);

	removeCommunicationRecordRequested$ = createEffect(() =>
		this.actions$.pipe(
			ofType(patientCommsActions.removeCommunicationRecordRequested),
			concatLatestFrom(() => [this.store.select(patientId)]),
			mergeMap(([action, patientId]) => this.patientsService.removeCommunicationRecord(patientId, action.recordId).pipe(
				map(() => patientCommsActions.removeCommunicationRecordCompleted({ patientId, recordId: action.recordId })),
				catchError((error: HttpErrorResponse) => of(clientErrored({ toastMessage: 'Remove communication record failed', errorMessage: this.errorService.getErrorMessage(error) })))
			))
		)
	);

	ownerContactNoteChanged$ = createEffect(() =>
		this.actions$.pipe(
			ofType(patientCommsActions.ownerContactNoteChanged),
			mergeMap(action => this.clientsService.setContactNote(action.clientId, action.contactNote).pipe(
				map(() => patientGraphActions.clientUpdated({ partialClient: { id: action.clientId, aggregateType: ClientTypeName, contactNote: action.contactNote } })),
				catchError((error: HttpErrorResponse) => of(clientErrored({ toastMessage: 'Set owner contact note failed', errorMessage: this.errorService.getErrorMessage(error) })))
			))
		)
	);
}
